/**
 * THE GEOMETRY CHART ANALYTICA MEASURES AGAINST — and it is the chart's own, not a second copy.
 *
 * <h2>The boundary ladder</h2>
 *
 * <pre>
 *   b3  · · · · · · · · · ·   high + 3h    the DOTTED outer bound
 *   b2  ───────────────────   high + 2h    the SOLID bound
 *   b1  ┌─────────────────┐   high + 1h    the flanking projection's far edge
 *   b0  ├─────────────────┤   high         THE MEASURED TOP
 *       ┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈┈   equator
 *   b0  ├─────────────────┤   low          THE MEASURED BOTTOM
 *   b1  └─────────────────┘   low  - 1h
 *   b2  ───────────────────   low  - 2h
 *   b3  · · · · · · · · · ·   low  - 3h
 * </pre>
 *
 * <h2>b2 and b3 are READ from `guideLevels`, never recomputed</h2>
 *
 * The chart draws those two levels from {@link guideLevels}, so computing `high + 2 * h` here as
 * well would be a second declaration of a number the renderer already owns — and the two would
 * agree on the day this was written and never again. The dimensions that count penetrations of b2
 * have to be counting penetrations of the line a reader can SEE, or the dashboard is auditing a
 * chart nobody is looking at. `analyticaGeometry.test.js` pins the identity.
 *
 * <p>b1 is the only level not in that helper, because it is the far edge of the flanking
 * rectangle rather than a line — {@link cycleRangeProjections} draws it as a box at one band
 * height. It is derived here from the same `h`, which is the one multiple that cannot drift
 * (a projection of one band height is what "one band height" means).
 *
 * <h2>A SECTION IS TESTED FROM WHERE THE MEASUREMENT ENDED, not from where it started</h2>
 *
 * A cycle band is MEASURED over its interval and DRAWN forward to the next cycle. Those are two
 * different spans deliberately, so a level can be seen to fail — and the tested span is a THIRD:
 * it begins the bar after the measurement closes, because the level does not exist until then.
 *
 * <pre>
 *   |<-- measured -->|<------------- tested ------------->|
 *   x1            xMeasuredTo                             x2 (clipped at the click)
 * </pre>
 *
 * <p>This was found by a fixture rather than by reading the code. Counting crossings over the
 * measuring bars is a LOOK-AHEAD and a guaranteed one: the equator is the midpoint of those very
 * bars' own extremes, so they cross it several times by construction, and the top and the bottom
 * are their own high and low so nothing can ever penetrate either. The equator tally came back
 * with four penetrations where two were real. The engine side states the same rule as
 * `validFrom = band.end + 1`.
 *
 * <p>The right edge is still clipped at the clicked bar, so a scan never reads a bar the reader
 * did not ask about — including the forward projection the final band of a run always carries.
 */

import { guideLevels, GUIDE_SOLID_MULTIPLE, GUIDE_DOTTED_MULTIPLE } from './bandGuides';

/** ECharts candlestick order: [open, close, low, high]. Reading it as OHLC takes the close. */
export const BAR_OPEN = 0;
export const BAR_CLOSE = 1;
export const BAR_LOW = 2;
export const BAR_HIGH = 3;

/** The four layers, outward from the measured edge. Index IS the multiple of h beyond it. */
export const BOUNDARY_KEYS = ['b0', 'b1', 'b2', 'b3'];

/**
 * How far beyond the measured edge each layer sits, in band heights.
 *
 * <p>b2 and b3 take the renderer's own constants rather than the literals 2 and 3, so a change to
 * where the chart draws its guides moves this ladder with it.
 */
export const BOUNDARY_MULTIPLES = [0, 1, GUIDE_SOLID_MULTIPLE, GUIDE_DOTTED_MULTIPLE];

/**
 * The full price ladder for one channel.
 *
 * @param {number} top the measured high
 * @param {number} bottom the measured low
 * @returns {{h:number, equator:number, up:number[], down:number[]}|null} null for a band with no
 *          height — a zero-height channel has every layer on one price, and an "excursion past
 *          b2" through a ladder like that is not a reading
 */
export function boundaryLevels(top, bottom) {
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null;
  const high = Math.max(top, bottom);
  const low = Math.min(top, bottom);
  const h = high - low;
  if (!(h > 0)) return null;

  // The renderer's own answer for the equator and the two outer bounds. Keyed by `kind`, which is
  // what the helper names them, so the mapping below says which line each layer IS.
  const guides = guideLevels(high, low);
  const upper = guides.filter(g => g.level > high).sort((a, b) => a.level - b.level);
  const lower = guides.filter(g => g.level < low).sort((a, b) => b.level - a.level);
  const equator = (guides.find(g => g.kind === 'equator') || {}).level;

  return {
    h,
    top: high,
    bottom: low,
    equator,
    // b0 and b1 from the measurement; b2 and b3 from the lines the chart draws.
    up: [high, high + h, upper[0]?.level, upper[1]?.level],
    down: [low, low - h, lower[0]?.level, lower[1]?.level],
  };
}

/**
 * The bands that govern the scanned range, each clipped to it and carrying its ladder.
 *
 * <p>A band whose whole span sits past the clicked bar is DROPPED rather than clipped to nothing —
 * it has no bars inside the scan, so every count it could contribute is zero and including it
 * would inflate the "sections" denominator with sections the scan never looked at.
 *
 * <p>The final band of a run projects forward past the last real bar by design; `upToIndex` is
 * what stops that projection being scanned, so the right edge is always a bar that exists.
 *
 * @param {Array<object>} bands the output of buildCycleBands — `{x1, x2, price1, price2}`
 * @param {number} upToIndex the clicked bar, INCLUSIVE
 * @param {string} category 'PTA' or 'PTB', carried through so a mixed scan stays attributable
 */
export function sectionsFrom(bands, upToIndex, category) {
  const out = [];
  if (!Array.isArray(bands)) return out;
  const last = Number.isFinite(upToIndex) ? Math.floor(upToIndex) : -1;
  if (last < 0) return out;

  bands.forEach((band) => {
    if (!band) return;
    const x1 = Number.isFinite(band.x1) ? band.x1 : null;
    const x2 = Number.isFinite(band.x2) ? band.x2 : null;
    if (x1 === null || x2 === null) return;
    const drawnFrom = Math.max(0, Math.min(x1, x2));
    // The bar AFTER the measurement closes. A band carrying no `xMeasuredTo` — an older caller,
    // or a hand-built fixture — falls back to its left edge, which is the previous behaviour and
    // is still safe: the look-ahead it admits is bounded by the band's own interval.
    const measuredTo = Number.isFinite(band.xMeasuredTo) ? band.xMeasuredTo : drawnFrom;
    const from = Math.max(drawnFrom, measuredTo + 1);
    if (from > last) return;                       // not testable yet within the scan
    const to = Math.min(Math.max(x1, x2), last);   // clipped, so no projection is scanned
    if (to <= from) return;

    const levels = boundaryLevels(band.price1, band.price2);
    if (!levels) return;

    out.push({
      category,
      from,
      to,
      // Where the RECTANGLE starts, which the overlay and the dashboard both report. Kept apart
      // from `from` because conflating the drawn span with the tested one is the defect above.
      drawnFrom,
      measuredTo,
      date1: band.date1,
      date2: band.date2,
      // CLIPPED is a property worth carrying: a section whose right edge is the clicked bar has
      // not finished, so a "never reached b1" reading of it is provisional rather than a result.
      clipped: Math.max(x1, x2) > last,
      ...levels,
    });
  });
  return out.sort((a, b) => a.from - b.from);
}

/**
 * Which section governs each bar of the scan.
 *
 * <p>Sections of one category can ABUT but should not overlap — a band runs to where the next
 * cycle begins. Where they do overlap (the final projected band against a later one), the LATER
 * section wins, because the reading is that a cycle's range stays in force until the next cycle
 * replaces it.
 *
 * @returns {Array<object|null>} one entry per bar index, 0..upToIndex
 */
export function sectionByBar(sections, upToIndex) {
  const last = Number.isFinite(upToIndex) ? Math.floor(upToIndex) : -1;
  const out = new Array(Math.max(0, last + 1)).fill(null);
  (Array.isArray(sections) ? sections : []).forEach((s) => {
    if (!s) return;
    for (let i = Math.max(0, s.from); i <= Math.min(s.to, last); i++) out[i] = s;
  });
  return out;
}

/**
 * Where a price sits on a section's ladder, as a SIGNED distance in band heights.
 *
 * <p>Zero is the equator, +0.5 the top, −0.5 the bottom, +1.5 the b1 line above. Signed and in
 * units of h so the number means the same thing on a 1993 channel and a 2026 one — the same
 * argument that put the structural tolerance in ATRs rather than in percent.
 */
export function heightsFromEquator(price, section) {
  if (!section || !Number.isFinite(price) || !(section.h > 0)) return null;
  return (price - section.equator) / section.h;
}

/**
 * The ZONE a price occupies, by distance from the channel.
 *
 * <pre>
 *   ZONE_1   inside the channel            between bottom and top
 *   ZONE_2   the first extension           beyond b0, not past b1
 *   ZONE_3   the second and third          past b1
 * </pre>
 *
 * <p>Three zones rather than four, as specified: the 2x and 3x levels are one zone, because what
 * they share — price is a long way outside the channel — is the thing the zone is for.
 *
 * <p>A price exactly ON b0 is ZONE_1. The channel is INCLUSIVE of its own edges, which is the
 * convention the band vocabulary already uses: a close on a line has not left the field.
 */
export const ZONES = ['ZONE_1', 'ZONE_2', 'ZONE_3'];

export function zoneOf(price, section) {
  if (!section || !Number.isFinite(price)) return null;
  const { top, bottom, up, down } = section;
  if (price <= top && price >= bottom) return 'ZONE_1';
  if (price > top) return price <= up[1] ? 'ZONE_2' : 'ZONE_3';
  return price >= down[1] ? 'ZONE_2' : 'ZONE_3';
}

/**
 * A fresh zero-filled tally keyed by the four boundary layers.
 *
 * <p>Declared once because six dimensions build one, and a tally missing a key reads downstream as
 * a layer that was never reached rather than as a layer nobody counted.
 */
export function emptyBoundaryTally() {
  return BOUNDARY_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {});
}
